/**
 * ComparisonView component - Side-by-side period comparison display.
 *
 * Shows metrics from two time periods with percentage changes,
 * trend indicators, and visual differentiation.
 */

import React from 'react';

interface PeriodData {
  label: string;
  avg: number;
  min: number;
  max: number;
}

interface ComparisonMetric {
  name: string;
  icon: string;
  unit: string;
  current: PeriodData;
  previous: PeriodData;
  percentageChange: number;
  trendDirection: 'improving' | 'declining' | 'stable';
}

interface ComparisonViewProps {
  metrics: ComparisonMetric[];
  className?: string;
}

const ComparisonView: React.FC<ComparisonViewProps> = ({ metrics, className = '' }) => {
  const getTrendIcon = (direction: string) => {
    switch (direction) {
      case 'improving':
        return '📈';
      case 'declining':
        return '📉';
      default:
        return '➡️';
    }
  };

  const getTrendColor = (direction: string, change: number) => {
    if (direction === 'stable') return 'text-neutral-600';
    return change > 0 ? 'text-green-700' : 'text-red-700';
  };

  const getChangeColor = (change: number) => {
    if (Math.abs(change) < 5) return 'bg-neutral-100 text-neutral-700';
    return change > 0
      ? 'bg-green-100 text-green-800 border-green-300'
      : 'bg-red-100 text-red-800 border-red-300';
  };

  return (
    <div className={`card shadow-elegant ${className}`}>
      <div className="mb-6">
        <h3 className="text-2xl font-serif font-bold text-neutral-900 mb-2">
          📊 Period Comparison
        </h3>
        <p className="text-neutral-600">
          Compare current period with previous period to track changes
        </p>
      </div>

      <div className="space-y-6">
        {metrics.map((metric) => (
          <div
            key={metric.name}
            className="border border-neutral-200 rounded-xl p-5 bg-gradient-to-br from-neutral-50 to-white hover:shadow-soft transition-shadow duration-200"
          >
            {/* Metric Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{metric.icon}</span>
                <h4 className="text-lg font-semibold text-neutral-900">{metric.name}</h4>
              </div>
              <div
                className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg border font-semibold text-sm ${getChangeColor(
                  metric.percentageChange
                )}`}
              >
                <span>{getTrendIcon(metric.trendDirection)}</span>
                <span>
                  {metric.percentageChange > 0 && '+'}
                  {metric.percentageChange.toFixed(1)}%
                </span>
              </div>
            </div>

            {/* Period Comparison Grid */}
            <div className="grid grid-cols-2 gap-4">
              {/* Current Period */}
              <div className="bg-gradient-to-br from-primary-50 to-white border-2 border-primary-300 rounded-xl p-4">
                <div className="text-xs text-neutral-600 font-semibold mb-2 uppercase tracking-wide">
                  {metric.current.label}
                </div>
                <div className="flex items-baseline space-x-2 mb-3">
                  <div className="text-3xl font-bold text-primary-700">
                    {metric.current.avg.toFixed(1)}
                  </div>
                  <span className="text-sm text-neutral-500 font-medium">{metric.unit}</span>
                </div>
                <div className="flex items-center justify-between text-xs text-neutral-600">
                  <span>Min: {metric.current.min.toFixed(1)}</span>
                  <span>Max: {metric.current.max.toFixed(1)}</span>
                </div>
              </div>

              {/* Previous Period */}
              <div className="bg-gradient-to-br from-neutral-50 to-white border border-neutral-200 rounded-xl p-4">
                <div className="text-xs text-neutral-600 font-semibold mb-2 uppercase tracking-wide">
                  {metric.previous.label}
                </div>
                <div className="flex items-baseline space-x-2 mb-3">
                  <div className="text-3xl font-bold text-neutral-700">
                    {metric.previous.avg.toFixed(1)}
                  </div>
                  <span className="text-sm text-neutral-500 font-medium">{metric.unit}</span>
                </div>
                <div className="flex items-center justify-between text-xs text-neutral-600">
                  <span>Min: {metric.previous.min.toFixed(1)}</span>
                  <span>Max: {metric.previous.max.toFixed(1)}</span>
                </div>
              </div>
            </div>

            {/* Trend Interpretation */}
            <div className={`mt-3 text-sm font-medium ${getTrendColor(metric.trendDirection, metric.percentageChange)}`}>
              {metric.trendDirection === 'improving' &&
                `${metric.name} is improving compared to the previous period`}
              {metric.trendDirection === 'declining' &&
                `${metric.name} has declined compared to the previous period`}
              {metric.trendDirection === 'stable' && `${metric.name} remains stable`}
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {metrics.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📊</div>
          <p className="text-lg text-neutral-600">
            Complete more check-ins to enable period comparison
          </p>
        </div>
      )}
    </div>
  );
};

export default ComparisonView;
